/**
 * `results` i18n namespace — FR default + AR (RTL). Registered once via the module's onRegister.
 * Flat dot-keyed; the shell mirrors layout under dir="rtl" via CSS logical properties, so copy here
 * is direction-agnostic. Mono numerics (numero, hash, montants) stay LTR with nv-ltr-island in TSX.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav + titres
  "nav.section.production": "Production",
  "nav.results": "Comptes-rendus",
  "results.console.title": "Comptes-rendus",
  "results.console.subtitle": "Émission, rendu et diffusion des comptes-rendus",
  "results.cr.title": "Compte-rendu {numero}",
  "results.cr.titleFallback": "Compte-rendu",
  "results.emission.title": "Émettre un compte-rendu",

  // recherche / console
  "results.lookup.legend": "Ouvrir un compte-rendu",
  "results.lookup.crId": "Identifiant du compte-rendu",
  "results.lookup.crIdHint": "L'identifiant numérique du compte-rendu à consulter.",
  "results.lookup.demandeId": "Identifiant de la demande",
  "results.lookup.demandeIdHint": "Pour émettre un compte-rendu à partir d'une demande validée.",
  "results.lookup.open": "Ouvrir",
  "results.lookup.emit": "Émettre",
  "results.lookup.invalidId": "Saisissez un identifiant numérique valide.",
  "results.recent.title": "Récemment consultés",
  "results.recent.empty": "Aucun compte-rendu consulté récemment.",
  "results.recent.clear": "Effacer l'historique",
  "results.recent.openedAt": "Ouvert le {date}",

  // LAB-08 — la file « à émettre » (demandes validées sans compte-rendu à jour)
  "results.pending.title": "À émettre",
  "results.pending.subtitle":
    "Demandes validées en attente d'un compte-rendu à jour — cliquez pour émettre, sans saisir d'identifiant.",
  "results.pending.count": "{n} en attente",
  "results.pending.col.demande": "Demande",
  "results.pending.col.validated": "Résultats validés",
  "results.pending.col.reason": "Motif",
  "results.pending.col.lastValidation": "Dernière validation",
  "results.pending.reason.jamais_emis": "Jamais émis",
  "results.pending.reason.resultats_neufs": "Nouveaux résultats (v{version} émise)",
  "results.pending.emit": "Émettre",
  "results.pending.empty.title": "Rien à émettre",
  "results.pending.empty.desc": "Aucune demande validée n'attend un compte-rendu à jour.",
  // Shown when the /results/pending endpoint is not deployed on this install (404/501) — the id-lookup
  // above still works, so the console degrades gracefully instead of erroring.
  "results.pending.unavailable":
    "La file « à émettre » n'est pas disponible sur cette installation. Utilisez la recherche par identifiant ci-dessus.",
  "results.pending.error": "Impossible de charger la file « à émettre ».",

  // états compte-rendu
  "results.etat.genere": "Généré (non émis)",
  "results.etat.emis": "Émis",
  "results.etat.supersede": "Remplacé",
  "results.type.partiel": "Partiel",
  "results.type.complet": "Complet",
  "results.version": "Version {n}",
  "results.numero": "N° {numero}",

  // aperçu / rendu
  "results.preview.title": "Aperçu du rendu",
  "results.preview.lines": "Lignes du compte-rendu",
  "results.preview.col.code": "Analyse",
  "results.preview.col.libelle": "Libellé",
  "results.preview.col.valeur": "Résultat",
  "results.preview.col.unite": "Unité",
  "results.preview.col.intervalle": "Intervalle",
  "results.preview.col.drapeau": "Drapeau",
  "results.preview.noLines": "Ce compte-rendu ne contient aucune ligne.",
  "results.preview.frozen": "Composition figée au rendu (RES-4) — valeurs non recalculées.",
  "results.preview.signature": "Empreinte de signature",
  "results.preview.emisAt": "Émis le {date}",
  "results.preview.notEmitted": "Compte-rendu généré — l'artefact n'est disponible qu'après émission.",

  // drapeaux cliniques
  "results.drapeau.normal": "Dans l'intervalle",
  "results.drapeau.bas": "Bas",
  "results.drapeau.haut": "Haut",
  "results.drapeau.critique": "Valeur panique",
  "results.drapeau.aucun": "—",

  // émission
  "results.emission.legend": "Paramètres d'émission",
  "results.emission.demande": "Demande",
  "results.emission.scope": "Portée",
  "results.emission.scope.partiel": "Partiel (résultats validés disponibles)",
  "results.emission.scope.complet": "Complet (dossier clôturé)",
  "results.emission.scopeHint": "Le partiel émet une version progressive ; le complet clôt le dossier.",
  // NOVA-057 — the report type is derived server-side from the validated-result coverage (VAL-6).
  "results.emission.derivedNote":
    "Le type du compte-rendu (partiel ou complet) est déterminé automatiquement selon les résultats déjà validés.",
  "results.emission.submit": "Émettre le compte-rendu",
  "results.emission.confirmTitle": "Confirmer l'émission",
  "results.emission.confirmBody":
    "L'émission fige le compte-rendu et ouvre les canaux de diffusion. Cette version devient immuable. Continuer ?",
  "results.emission.confirm": "Émettre",
  "results.emission.success": "Compte-rendu {numero} émis (v{version}).",
  "results.emission.noResult": "Aucun résultat validé à émettre pour cette demande.",
  "results.emission.reason.aucun_resultat_valide": "Aucun résultat validé disponible.",
  "results.emission.error": "Échec de l'émission du compte-rendu.",
  "results.emission.back": "Retour à la console",

  // diffusion
  "results.diffusion.title": "Diffusion par canal",
  "results.diffusion.subtitle": "Comptoir, portail patient et Novera MED",
  "results.diffusion.col.canal": "Canal",
  "results.diffusion.col.statut": "Statut",
  "results.diffusion.col.detail": "Détail",
  "results.diffusion.col.actions": "Actions",
  "results.diffusion.empty": "Aucun canal ouvert — émettez le compte-rendu pour ouvrir les canaux.",
  "results.diffusion.onlyEmitted": "Les canaux ne sont disponibles que pour un compte-rendu émis.",
  "results.canal.comptoir": "Comptoir",
  "results.canal.portail": "Portail patient",
  "results.canal.novera": "Novera MED",
  "results.canal.remote": "Distant",
  "results.canal.comptoir.desc": "Présence physique = consentement (non soumis à recueil).",
  "results.canal.portail.desc": "Canal distant — soumis au consentement central.",
  "results.canal.novera.desc": "Canal distant — soumis au consentement central.",

  "results.statut.en_attente": "En attente",
  "results.statut.retenu": "Retenu (quarantaine)",
  "results.statut.libere": "Libéré",
  "results.statut.delivre": "Délivré",
  "results.statut.echec": "Échec",
  "results.statut.deliveredAt": "Délivré le {date}",
  "results.statut.heldUntil": "Libération automatique le {date}",
  "results.statut.motif": "Motif : {motif}",
  "results.motif.consentement_absent": "Consentement absent (canal distant).",

  // actions canal
  "results.canal.deliver": "Délivrer",
  "results.canal.release": "Libérer",
  "results.canal.criticalHold": "Retenir si valeur panique non acquittée",
  "results.canal.criticalHoldHint":
    "Une valeur panique non acquittée met le canal patient en quarantaine (libération à expiration du délai).",
  // NOVA-055 — the server-derived hold banner (the hold is no longer an operator toggle; it is derived
  // from the demande's open critical escalations).
  "results.canal.criticalUnacked": "Valeur panique non acquittée — canal patient retenu (quarantaine).",
  "results.canal.deliverConfirmTitle": "Confirmer la diffusion",
  "results.canal.deliverConfirmBody": "Diffuser le compte-rendu sur le canal « {canal} » ?",
  "results.canal.releaseConfirmTitle": "Confirmer la libération",
  "results.canal.releaseConfirmBody":
    "Lever la quarantaine et libérer le canal « {canal} » (la boucle critique est close) ?",
  "results.canal.deliverSuccess": "Canal « {canal} » : {statut}.",
  "results.canal.releaseSuccess": "Canal « {canal} » libéré.",
  "results.canal.deliverError": "Échec de la diffusion du canal.",
  "results.canal.releaseError": "Échec de la libération du canal.",

  // impression comptoir
  "results.print.title": "Impression comptoir",
  "results.print.subtitle": "Rendu HTML gouverné, regénéré à partir des lignes figées.",
  "results.print.open": "Ouvrir l'impression",
  "results.print.openNewTab": "Ouvrir dans un nouvel onglet",
  "results.print.print": "Imprimer",
  "results.print.notEmitted": "Le compte-rendu doit être émis avant l'impression.",
  "results.print.loading": "Génération du rendu…",
  "results.print.error": "Impossible de générer le rendu d'impression.",
  "results.print.empty": "Aucun rendu disponible.",
  "results.print.frame": "Aperçu d'impression du compte-rendu",

  // divers
  "results.notFound": "Compte-rendu introuvable.",
  "results.notFoundDesc": "Aucun compte-rendu ne correspond à cet identifiant pour ce laboratoire.",
  "results.action.refresh": "Actualiser",
  "results.action.cancel": "Annuler",
  "results.action.copy": "Copier",
  "results.copied": "Copié dans le presse-papiers.",
  "results.tab.preview": "Aperçu",
  "results.tab.diffusion": "Diffusion",
  "results.tab.print": "Impression",
  "results.meta.demande": "Demande",
  "results.meta.laboratoire": "Laboratoire",
  "results.meta.emisAt": "Émis le",

  // NOVA-054 — comptoir desk-recall worklist (D-24)
  "nav.results.recall": "Rappels au comptoir",
  "results.recall.title": "Rappels au comptoir (D-24)",
  "results.recall.subtitle":
    "Comptes-rendus ré-émis dont une copie au comptoir a déjà été remise : rappelez la copie superseded et remettez la version corrigée.",
  "results.recall.queue": "Rappels en attente",
  "results.recall.pending": "À rappeler",
  "results.recall.col.numero": "N° de compte-rendu",
  "results.recall.col.version": "Version corrigée",
  "results.recall.col.recalled": "Version remise (rappelée)",
  "results.recall.col.status": "Statut",
  "results.recall.openCr": "Ouvrir le CR",
  "results.recall.recover": "Marquer récupéré",
  "results.recall.recovered": "Copie récupérée — rappel clôturé.",
  "results.recall.alreadyRecovered": "Rappel déjà clôturé.",
  "results.recall.error": "Échec de la récupération.",
  "results.recall.empty.title": "Aucun rappel au comptoir",
  "results.recall.empty.desc": "Aucune copie remise au comptoir n'est en attente de rappel.",
};

const AR: Record<string, string> = {
  "nav.section.production": "الإنتاج",
  "nav.results": "التقارير",
  "results.console.title": "التقارير",
  "results.console.subtitle": "إصدار التقارير وعرضها وتوزيعها",
  "results.cr.title": "التقرير {numero}",
  "results.cr.titleFallback": "التقرير",
  "results.emission.title": "إصدار تقرير",

  "results.lookup.legend": "فتح تقرير",
  "results.lookup.crId": "معرّف التقرير",
  "results.lookup.crIdHint": "المعرّف الرقمي للتقرير المراد عرضه.",
  "results.lookup.demandeId": "معرّف الطلب",
  "results.lookup.demandeIdHint": "لإصدار تقرير انطلاقًا من طلب مُصادَق عليه.",
  "results.lookup.open": "فتح",
  "results.lookup.emit": "إصدار",
  "results.lookup.invalidId": "أدخل معرّفًا رقميًا صحيحًا.",
  "results.recent.title": "المعروضة مؤخرًا",
  "results.recent.empty": "لا يوجد تقرير معروض مؤخرًا.",
  "results.recent.clear": "مسح السجل",
  "results.recent.openedAt": "فُتح في {date}",

  // LAB-08 — قائمة «للإصدار» (طلبات مُصادَقة بلا تقرير مُحدَّث)
  "results.pending.title": "للإصدار",
  "results.pending.subtitle":
    "طلبات مُصادَقة بانتظار تقرير مُحدَّث — انقر للإصدار دون إدخال معرّف.",
  "results.pending.count": "{n} قيد الانتظار",
  "results.pending.col.demande": "الطلب",
  "results.pending.col.validated": "النتائج المُصادَقة",
  "results.pending.col.reason": "السبب",
  "results.pending.col.lastValidation": "آخر مصادقة",
  "results.pending.reason.jamais_emis": "لم يُصدَر بعد",
  "results.pending.reason.resultats_neufs": "نتائج جديدة (صدر الإصدار {version})",
  "results.pending.emit": "إصدار",
  "results.pending.empty.title": "لا شيء للإصدار",
  "results.pending.empty.desc": "لا يوجد طلب مُصادَق بانتظار تقرير مُحدَّث.",
  "results.pending.unavailable":
    "قائمة «للإصدار» غير متوفرة على هذا التثبيت. استخدم البحث بالمعرّف أعلاه.",
  "results.pending.error": "تعذّر تحميل قائمة «للإصدار».",

  "results.etat.genere": "مُولَّد (غير مُصدَر)",
  "results.etat.emis": "مُصدَر",
  "results.etat.supersede": "مُستبدَل",
  "results.type.partiel": "جزئي",
  "results.type.complet": "كامل",
  "results.version": "الإصدار {n}",
  "results.numero": "رقم {numero}",

  "results.preview.title": "معاينة العرض",
  "results.preview.lines": "سطور التقرير",
  "results.preview.col.code": "التحليل",
  "results.preview.col.libelle": "التسمية",
  "results.preview.col.valeur": "النتيجة",
  "results.preview.col.unite": "الوحدة",
  "results.preview.col.intervalle": "المجال",
  "results.preview.col.drapeau": "العلامة",
  "results.preview.noLines": "لا يحتوي هذا التقرير على أي سطر.",
  "results.preview.frozen": "تركيب مُجمَّد عند العرض (RES-4) — القيم غير مُعاد حسابها.",
  "results.preview.signature": "بصمة التوقيع",
  "results.preview.emisAt": "صدر في {date}",
  "results.preview.notEmitted": "تقرير مُولَّد — لا يتوفر المستند إلا بعد الإصدار.",

  "results.drapeau.normal": "ضمن المجال",
  "results.drapeau.bas": "منخفض",
  "results.drapeau.haut": "مرتفع",
  "results.drapeau.critique": "قيمة حرجة",
  "results.drapeau.aucun": "—",

  "results.emission.legend": "معايير الإصدار",
  "results.emission.demande": "الطلب",
  "results.emission.scope": "النطاق",
  "results.emission.scope.partiel": "جزئي (النتائج المُصادَقة المتوفرة)",
  "results.emission.scope.complet": "كامل (الملف مُغلق)",
  "results.emission.scopeHint": "الجزئي يُصدر إصدارًا تدريجيًا؛ الكامل يُغلق الملف.",
  // NOVA-057
  "results.emission.derivedNote":
    "يُحدَّد نوع التقرير (جزئي أو كامل) تلقائيًا حسب النتائج المُصادَق عليها.",
  "results.emission.submit": "إصدار التقرير",
  "results.emission.confirmTitle": "تأكيد الإصدار",
  "results.emission.confirmBody":
    "يُجمِّد الإصدار التقرير ويفتح قنوات التوزيع. يصبح هذا الإصدار غير قابل للتعديل. متابعة؟",
  "results.emission.confirm": "إصدار",
  "results.emission.success": "تم إصدار التقرير {numero} (الإصدار {version}).",
  "results.emission.noResult": "لا توجد نتيجة مُصادَقة لإصدارها لهذا الطلب.",
  "results.emission.reason.aucun_resultat_valide": "لا توجد نتيجة مُصادَقة متوفرة.",
  "results.emission.error": "فشل إصدار التقرير.",
  "results.emission.back": "العودة إلى الواجهة",

  "results.diffusion.title": "التوزيع حسب القناة",
  "results.diffusion.subtitle": "الكاونتر، بوابة المريض و Novera MED",
  "results.diffusion.col.canal": "القناة",
  "results.diffusion.col.statut": "الحالة",
  "results.diffusion.col.detail": "التفصيل",
  "results.diffusion.col.actions": "إجراءات",
  "results.diffusion.empty": "لا توجد قناة مفتوحة — أصدر التقرير لفتح القنوات.",
  "results.diffusion.onlyEmitted": "تتوفر القنوات فقط لتقرير مُصدَر.",
  "results.canal.comptoir": "الكاونتر",
  "results.canal.portail": "بوابة المريض",
  "results.canal.novera": "Novera MED",
  "results.canal.remote": "بعيدة",
  "results.canal.comptoir.desc": "الحضور المادي = موافقة (دون استئذان).",
  "results.canal.portail.desc": "قناة بعيدة — خاضعة للموافقة المركزية.",
  "results.canal.novera.desc": "قناة بعيدة — خاضعة للموافقة المركزية.",

  "results.statut.en_attente": "قيد الانتظار",
  "results.statut.retenu": "محتجز (حجر)",
  "results.statut.libere": "مُحرَّر",
  "results.statut.delivre": "مُسلَّم",
  "results.statut.echec": "فشل",
  "results.statut.deliveredAt": "سُلِّم في {date}",
  "results.statut.heldUntil": "تحرير تلقائي في {date}",
  "results.statut.motif": "السبب: {motif}",
  "results.motif.consentement_absent": "غياب الموافقة (قناة بعيدة).",

  "results.canal.deliver": "تسليم",
  "results.canal.release": "تحرير",
  "results.canal.criticalHold": "احتجاز إذا كانت القيمة الحرجة غير مُؤكَّدة",
  "results.canal.criticalHoldHint":
    "قيمة حرجة غير مُؤكَّدة تضع قناة المريض في الحجر (تحرير عند انتهاء المهلة).",
  // NOVA-055
  "results.canal.criticalUnacked": "قيمة حرجة غير مُؤكَّدة — قناة المريض محتجزة (حجر).",
  "results.canal.deliverConfirmTitle": "تأكيد التوزيع",
  "results.canal.deliverConfirmBody": "توزيع التقرير عبر القناة «{canal}»؟",
  "results.canal.releaseConfirmTitle": "تأكيد التحرير",
  "results.canal.releaseConfirmBody":
    "رفع الحجر وتحرير القناة «{canal}» (أُغلقت الحلقة الحرجة)؟",
  "results.canal.deliverSuccess": "القناة «{canal}»: {statut}.",
  "results.canal.releaseSuccess": "تم تحرير القناة «{canal}».",
  "results.canal.deliverError": "فشل توزيع القناة.",
  "results.canal.releaseError": "فشل تحرير القناة.",

  "results.print.title": "طباعة الكاونتر",
  "results.print.subtitle": "عرض HTML مُنظَّم، مُعاد توليده من السطور المُجمَّدة.",
  "results.print.open": "فتح الطباعة",
  "results.print.openNewTab": "فتح في علامة تبويب جديدة",
  "results.print.print": "طباعة",
  "results.print.notEmitted": "يجب إصدار التقرير قبل الطباعة.",
  "results.print.loading": "جارٍ توليد العرض…",
  "results.print.error": "تعذّر توليد عرض الطباعة.",
  "results.print.empty": "لا يتوفر أي عرض.",
  "results.print.frame": "معاينة طباعة التقرير",

  "results.notFound": "التقرير غير موجود.",
  "results.notFoundDesc": "لا يوجد تقرير مطابق لهذا المعرّف في هذا المختبر.",
  "results.action.refresh": "تحديث",
  "results.action.cancel": "إلغاء",
  "results.action.copy": "نسخ",
  "results.copied": "نُسخ إلى الحافظة.",
  "results.tab.preview": "المعاينة",
  "results.tab.diffusion": "التوزيع",
  "results.tab.print": "الطباعة",
  "results.meta.demande": "الطلب",
  "results.meta.laboratoire": "المختبر",
  "results.meta.emisAt": "صدر في",

  // NOVA-054 — سحب نسخ المكتب (D-24)
  "nav.results.recall": "عمليات السحب بالمكتب",
  "results.recall.title": "عمليات السحب بالمكتب (D-24)",
  "results.recall.subtitle":
    "تقارير أُعيد إصدارها وسُلِّمت نسخة منها بالمكتب: اسحب النسخة المُستبدَلة وسلِّم النسخة المصححة.",
  "results.recall.queue": "عمليات السحب قيد الانتظار",
  "results.recall.pending": "للسحب",
  "results.recall.col.numero": "رقم التقرير",
  "results.recall.col.version": "النسخة المصححة",
  "results.recall.col.recalled": "النسخة المُسلَّمة (المسحوبة)",
  "results.recall.col.status": "الحالة",
  "results.recall.openCr": "فتح التقرير",
  "results.recall.recover": "تعليم كمُستردّ",
  "results.recall.recovered": "تم استرداد النسخة — أُغلق السحب.",
  "results.recall.alreadyRecovered": "السحب مُغلق مسبقًا.",
  "results.recall.error": "فشل الاسترداد.",
  "results.recall.empty.title": "لا توجد عمليات سحب بالمكتب",
  "results.recall.empty.desc": "لا توجد نسخة مُسلَّمة بالمكتب بانتظار السحب.",
};

let registered = false;

/** Idempotently register the FR + AR namespace (called from the module's onRegister). */
export function registerResultsMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
